import { gql } from 'graphql-request';

// ==================== FRAGMENTS ====================

export const WAREHOUSE_FIELDS = gql`
  fragment WarehouseFields on WarehouseDto {
    warehouseId
    name
    description
    address
    lat
    long
    isActive
    isDefault
    organizationId
    createdAt
  }
`;

export const MATERIAL_CATEGORY_FIELDS = gql`
  fragment MaterialCategoryFields on MaterialCategoryDto {
    categoryId
    name
    description
    icon
    color
    type
    isActive
    sortOrder
    isGlobal
    calculatorType
    organizationId
    createdAt
  }
`;

export const MATERIAL_CATALOG_FIELDS = gql`
  fragment MaterialCatalogFields on MaterialCatalogDto {
    catalogId
    name
    sku
    description
    unit
    unitSystem
    averageCost
    defaultPricePerUnit
    reorderPoint
    suggestedOrderQuantity
    defaultSupplier
    leadTimeDays
    isActive
    categoryId
    organizationId
    createdAt
  }
`;

export const STOCK_LEVEL_FIELDS = gql`
  fragment StockLevelFields on StockLevelDto {
    stockLevelId
    quantityOnHand
    quantityReserved
    averageCost
    catalogId
    warehouseId
    lastReceivedAt
    lastConsumedAt
    updatedAt
  }
`;

export const STOCK_TRANSACTION_FIELDS = gql`
  fragment StockTransactionFields on StockTransactionDto {
    transactionId
    type
    status
    quantity
    unitCost
    totalCost
    referenceNumber
    notes
    reason
    supplier
    catalogId
    warehouseId
    toWarehouseId
    taskId
    projectId
    userId
    organizationId
    chargeToOrganizationId
    createdAt
  }
`;

export const TASK_MATERIAL_FIELDS = gql`
  fragment TaskMaterialFields on TaskMaterialDto {
    taskMaterialId
    source
    status
    catalogId
    warehouseId
    name
    supplier
    quantity
    unit
    costPerUnit
    pricePerUnit
    chargeToClient
    leadTimeDays
    time
    taskId
    transactionId
    organizationId
    createdAt
  }
`;

export const EQUIPMENT_ASSET_FIELDS = gql`
  fragment EquipmentAssetFields on EquipmentAssetDto {
    assetId
    name
    assetCode
    description
    serialNumber
    manufacturer
    model
    categoryId
    status
    purchaseCost
    purchaseDate
    hourlyRate
    clientHourlyRate
    warehouseId
    currentTaskId
    organizationId
    maintenanceNotes
    lastMaintenanceDate
    estimatedAvailableDate
    reservationStartDate
    reservationEndDate
    reservations {
      reservationId
      assetId
      taskId
      task {
        taskId
        name
        projectId
        project {
          projectId
          name
        }
      }
      startDate
      endDate
      notes
      status
      isCheckoutReservation
      createdAt
    }
    createdAt
  }
`;

export const EQUIPMENT_RESERVATION_FIELDS = gql`
  fragment EquipmentReservationFields on EquipmentReservationDto {
    reservationId
    assetId
    taskId
    task {
      taskId
      name
      projectId
      project {
        projectId
        name
      }
    }
    asset {
      assetId
      name
      assetCode
    }
    startDate
    endDate
    notes
    status
    isCheckoutReservation
    organizationId
    createdByUserId
    createdAt
  }
`;

export const EQUIPMENT_USAGE_FIELDS = gql`
  fragment EquipmentUsageFields on EquipmentUsageDto {
    usageId
    assetId
    taskId
    task {
      taskId
      name
      projectId
      project {
        projectId
        name
      }
    }
    checkoutDate
    returnDate
    expectedReturnDate
    hoursUsed
    chargeToClient
    costPerHour
    pricePerHour
    condition
    notes
    checkoutNotes
    checkoutUserId
    checkoutUser {
      userId
      firstName
      lastName
    }
    returnUserId
    returnUser {
      userId
      firstName
      lastName
    }
    createdAt
  }
`;

// ==================== WAREHOUSE QUERIES & MUTATIONS ====================

export const getWarehouses = gql`
  query getWarehouses {
    getWarehouses {
      ...WarehouseFields
    }
  }
  ${WAREHOUSE_FIELDS}
`;

export const getWarehouse = gql`
  query getWarehouse($warehouseId: String!) {
    getWarehouse(warehouseId: $warehouseId) {
      ...WarehouseFields
    }
  }
  ${WAREHOUSE_FIELDS}
`;

export const createWarehouse = gql`
  mutation createWarehouse($warehouse: WarehouseCreateInput!) {
    createWarehouse(warehouse: $warehouse) {
      ...WarehouseFields
    }
  }
  ${WAREHOUSE_FIELDS}
`;

export const updateWarehouse = gql`
  mutation updateWarehouse($warehouse: WarehouseUpdateInput!) {
    updateWarehouse(warehouse: $warehouse) {
      ...WarehouseFields
    }
  }
  ${WAREHOUSE_FIELDS}
`;

export const setDefaultWarehouse = gql`
  mutation setDefaultWarehouse($warehouseId: String!) {
    setDefaultWarehouse(warehouseId: $warehouseId) {
      ...WarehouseFields
    }
  }
  ${WAREHOUSE_FIELDS}
`;

export const deleteWarehouse = gql`
  mutation deleteWarehouse($warehouseId: String!) {
    deleteWarehouse(warehouseId: $warehouseId) {
      affected
    }
  }
`;

// ==================== MATERIAL CATEGORY QUERIES & MUTATIONS ====================

export const getMaterialCategories = gql`
  query getMaterialCategories {
    getMaterialCategories {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const getMaterialCategoriesByType = gql`
  query getMaterialCategoriesByType($type: CategoryType!) {
    getMaterialCategoriesByType(type: $type) {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const createMaterialCategory = gql`
  mutation createMaterialCategory($category: MaterialCategoryCreateInput!) {
    createMaterialCategory(category: $category) {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const updateMaterialCategory = gql`
  mutation updateMaterialCategory($category: MaterialCategoryUpdateInput!) {
    updateMaterialCategory(category: $category) {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const deleteMaterialCategory = gql`
  mutation deleteMaterialCategory($categoryId: String!) {
    deleteMaterialCategory(categoryId: $categoryId) {
      affected
    }
  }
`;

// ==================== GLOBAL MATERIAL CATEGORY QUERIES & MUTATIONS ====================

export const getGlobalMaterialCategories = gql`
  query getGlobalMaterialCategories {
    getGlobalMaterialCategories {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const createGlobalMaterialCategory = gql`
  mutation createGlobalMaterialCategory($category: MaterialCategoryCreateInput!) {
    createGlobalMaterialCategory(category: $category) {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const updateGlobalMaterialCategory = gql`
  mutation updateGlobalMaterialCategory($category: MaterialCategoryUpdateInput!) {
    updateGlobalMaterialCategory(category: $category) {
      ...MaterialCategoryFields
    }
  }
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const deleteGlobalMaterialCategory = gql`
  mutation deleteGlobalMaterialCategory($categoryId: String!) {
    deleteGlobalMaterialCategory(categoryId: $categoryId) {
      affected
    }
  }
`;

// ==================== MATERIAL CATALOG QUERIES & MUTATIONS ====================

export const getMaterialCatalog = gql`
  query getMaterialCatalog {
    getMaterialCatalog {
      ...MaterialCatalogFields
      category {
        ...MaterialCategoryFields
      }
    }
  }
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const getMaterialCatalogItem = gql`
  query getMaterialCatalogItem($catalogId: String!) {
    getMaterialCatalogItem(catalogId: $catalogId) {
      ...MaterialCatalogFields
      category {
        ...MaterialCategoryFields
      }
    }
  }
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const searchMaterialCatalog = gql`
  query searchMaterialCatalog($search: String!) {
    searchMaterialCatalog(search: $search) {
      ...MaterialCatalogFields
    }
  }
  ${MATERIAL_CATALOG_FIELDS}
`;

export const getMaterialCatalogByCalculatorType = gql`
  query getMaterialCatalogByCalculatorType($calculatorType: String!) {
    getMaterialCatalogByCalculatorType(calculatorType: $calculatorType) {
      ...MaterialCatalogFields
      category {
        ...MaterialCategoryFields
      }
    }
  }
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
`;

export const createMaterialCatalogItem = gql`
  mutation createMaterialCatalogItem($catalog: MaterialCatalogCreateInput!) {
    createMaterialCatalogItem(catalog: $catalog) {
      ...MaterialCatalogFields
    }
  }
  ${MATERIAL_CATALOG_FIELDS}
`;

export const updateMaterialCatalogItem = gql`
  mutation updateMaterialCatalogItem($catalog: MaterialCatalogUpdateInput!) {
    updateMaterialCatalogItem(catalog: $catalog) {
      ...MaterialCatalogFields
    }
  }
  ${MATERIAL_CATALOG_FIELDS}
`;

export const deleteMaterialCatalogItem = gql`
  mutation deleteMaterialCatalogItem($catalogId: String!) {
    deleteMaterialCatalogItem(catalogId: $catalogId) {
      affected
    }
  }
`;

// ==================== STOCK LEVEL QUERIES ====================

export const getStockLevels = gql`
  query getAllStockLevels {
    getAllStockLevels {
      ...StockLevelFields
      catalog {
        ...MaterialCatalogFields
        category {
          ...MaterialCategoryFields
        }
      }
      warehouse {
        ...WarehouseFields
      }
    }
  }
  ${STOCK_LEVEL_FIELDS}
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

export const getStockLevelsByWarehouse = gql`
  query getStockLevelsByWarehouse($warehouseId: String!) {
    getStockLevelsByWarehouse(warehouseId: $warehouseId) {
      ...StockLevelFields
      catalog {
        ...MaterialCatalogFields
        category {
          ...MaterialCategoryFields
        }
      }
      warehouse {
        ...WarehouseFields
      }
    }
  }
  ${STOCK_LEVEL_FIELDS}
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

export const getStockLevel = gql`
  query getStockLevel($catalogId: String!, $warehouseId: String!) {
    getStockLevel(catalogId: $catalogId, warehouseId: $warehouseId) {
      ...StockLevelFields
    }
  }
  ${STOCK_LEVEL_FIELDS}
`;

export const getStockSummary = gql`
  query getStockSummary($catalogId: String!) {
    getStockSummary(catalogId: $catalogId) {
      catalogId
      catalogName
      totalOnHand
      totalReserved
      totalAvailable
      averageCost
      warehouseCount
    }
  }
`;

export const getLowStockItems = gql`
  query getLowStockItems {
    getLowStockItems {
      ...StockLevelFields
      catalog {
        ...MaterialCatalogFields
        category {
          ...MaterialCategoryFields
        }
      }
      warehouse {
        ...WarehouseFields
      }
    }
  }
  ${STOCK_LEVEL_FIELDS}
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

// ==================== STOCK TRANSACTION MUTATIONS ====================

export const receiveStock = gql`
  mutation receiveStock($input: ReceiveStockInput!) {
    receiveStock(input: $input) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const consumeStock = gql`
  mutation consumeStock($input: ConsumeStockInput!) {
    consumeStock(input: $input) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const adjustStock = gql`
  mutation adjustStock($input: AdjustStockInput!) {
    adjustStock(input: $input) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const reserveStock = gql`
  mutation reserveStock($input: ReserveStockInput!) {
    reserveStock(input: $input) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const releaseReservation = gql`
  mutation releaseStockReservation($transactionId: String!) {
    releaseStockReservation(transactionId: $transactionId) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const returnStock = gql`
  mutation returnStock($input: ReturnStockInput!) {
    returnStock(input: $input) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const transferStock = gql`
  mutation transferStock($input: TransferStockInput!) {
    transferStock(input: $input) {
      ...StockTransactionFields
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
`;

export const getStockTransactions = gql`
  query getStockTransactions($filter: StockTransactionFilterInput) {
    getStockTransactions(filter: $filter) {
      ...StockTransactionFields
      catalog {
        ...MaterialCatalogFields
        category {
          ...MaterialCategoryFields
        }
      }
      warehouse {
        ...WarehouseFields
      }
      project {
        projectId
        name
      }
      task {
        taskId
        name
      }
      user {
        userId
        firstName
        lastName
      }
    }
  }
  ${STOCK_TRANSACTION_FIELDS}
  ${MATERIAL_CATALOG_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

// ==================== TASK MATERIAL QUERIES & MUTATIONS ====================

export const getTaskMaterials = gql`
  query getTaskMaterials($taskId: String!) {
    getTaskMaterials(taskId: $taskId) {
      ...TaskMaterialFields
      catalog {
        ...MaterialCatalogFields
      }
      warehouse {
        ...WarehouseFields
      }
    }
  }
  ${TASK_MATERIAL_FIELDS}
  ${MATERIAL_CATALOG_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

export const getTaskMaterialSummary = gql`
  query getTaskMaterialSummary($taskId: String!) {
    getTaskMaterialSummary(taskId: $taskId) {
      totalCost
      totalPrice
      warehouseMaterialsCount
      manualMaterialsCount
    }
  }
`;

export const createTaskMaterialManual = gql`
  mutation createTaskMaterialManual($input: TaskMaterialManualInput!) {
    createTaskMaterialManual(input: $input) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

export const createTaskMaterialFromWarehouse = gql`
  mutation createTaskMaterialFromWarehouse($input: TaskMaterialWarehouseInput!) {
    createTaskMaterialFromWarehouse(input: $input) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

export const createTaskMaterialPlanned = gql`
  mutation createTaskMaterialPlanned($input: TaskMaterialPlannedInput!) {
    createTaskMaterialPlanned(input: $input) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

export const updateTaskMaterial = gql`
  mutation updateTaskMaterial($input: TaskMaterialUpdateInput!) {
    updateTaskMaterial(input: $input) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

export const returnTaskMaterial = gql`
  mutation returnTaskMaterialToWarehouse($input: TaskMaterialReturnInput!) {
    returnTaskMaterialToWarehouse(input: $input) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

export const deleteTaskMaterial = gql`
  mutation deleteTaskMaterial($taskMaterialId: String!) {
    deleteTaskMaterial(taskMaterialId: $taskMaterialId) {
      affected
    }
  }
`;

export const reserveTaskMaterial = gql`
  mutation reserveTaskMaterial($taskMaterialId: String!) {
    reserveTaskMaterial(taskMaterialId: $taskMaterialId) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

export const consumeTaskMaterial = gql`
  mutation consumeTaskMaterial($taskMaterialId: String!) {
    consumeTaskMaterial(taskMaterialId: $taskMaterialId) {
      ...TaskMaterialFields
    }
  }
  ${TASK_MATERIAL_FIELDS}
`;

// ==================== TASK EQUIPMENT QUERIES & MUTATIONS ====================

export const TASK_EQUIPMENT_FIELDS = gql`
  fragment TaskEquipmentFields on TaskEquipmentDto {
    taskEquipmentId
    source
    status
    assetId
    categoryId
    warehouseId
    name
    description
    estimatedHours
    costPerHour
    pricePerHour
    chargeToClient
    taskId
    usageId
    reservationId
    organizationId
    totalCost
    totalPrice
    createdAt
  }
`;

export const getTaskEquipments = gql`
  query getTaskEquipments($taskId: String!) {
    getTaskEquipments(taskId: $taskId) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const getTaskEquipment = gql`
  query getTaskEquipment($taskEquipmentId: String!) {
    getTaskEquipment(taskEquipmentId: $taskEquipmentId) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const getTaskEquipmentSummary = gql`
  query getTaskEquipmentSummary($taskId: String!) {
    getTaskEquipmentSummary(taskId: $taskId) {
      totalCost
      totalPrice
      warehouseEquipmentCount
      manualEquipmentCount
    }
  }
`;

export const createTaskEquipmentManual = gql`
  mutation createTaskEquipmentManual($input: TaskEquipmentManualInput!) {
    createTaskEquipmentManual(input: $input) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const createTaskEquipmentFromWarehouse = gql`
  mutation createTaskEquipmentFromWarehouse($input: TaskEquipmentWarehouseInput!) {
    createTaskEquipmentFromWarehouse(input: $input) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const createTaskEquipmentPlanned = gql`
  mutation createTaskEquipmentPlanned($input: TaskEquipmentPlannedInput!) {
    createTaskEquipmentPlanned(input: $input) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const updateTaskEquipment = gql`
  mutation updateTaskEquipment($input: TaskEquipmentUpdateInput!) {
    updateTaskEquipment(input: $input) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const assignTaskEquipmentAsset = gql`
  mutation assignTaskEquipmentAsset($input: TaskEquipmentAssignAssetInput!) {
    assignTaskEquipmentAsset(input: $input) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const checkoutTaskEquipment = gql`
  mutation checkoutTaskEquipment($taskEquipmentId: String!) {
    checkoutTaskEquipment(taskEquipmentId: $taskEquipmentId) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const returnTaskEquipment = gql`
  mutation returnTaskEquipment($input: TaskEquipmentReturnInput!) {
    returnTaskEquipment(input: $input) {
      ...TaskEquipmentFields
    }
  }
  ${TASK_EQUIPMENT_FIELDS}
`;

export const deleteTaskEquipment = gql`
  mutation deleteTaskEquipment($taskEquipmentId: String!) {
    deleteTaskEquipment(taskEquipmentId: $taskEquipmentId) {
      affected
    }
  }
`;

// ==================== EQUIPMENT ASSET QUERIES & MUTATIONS ====================

export const getEquipmentAssets = gql`
  query getEquipmentAssets {
    getEquipmentAssets {
      ...EquipmentAssetFields
      category {
        ...MaterialCategoryFields
      }
      warehouse {
        ...WarehouseFields
      }
      currentTask {
        taskId
        name
        projectId
        originalTaskId
        project {
          projectId
          name
        }
      }
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

export const getEquipmentAssetsByStatus = gql`
  query getEquipmentAssetsByStatus($status: EquipmentStatus!) {
    getEquipmentAssetsByStatus(status: $status) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const getAvailableEquipment = gql`
  query getAvailableEquipment {
    getAvailableEquipment {
      ...EquipmentAssetFields
      warehouse {
        ...WarehouseFields
      }
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

export const getEquipmentByTask = gql`
  query getEquipmentByTask($taskId: String!) {
    getEquipmentByTask(taskId: $taskId) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const getEquipmentAsset = gql`
  query getEquipmentAsset($assetId: String!) {
    getEquipmentAsset(assetId: $assetId) {
      ...EquipmentAssetFields
      category {
        ...MaterialCategoryFields
      }
      warehouse {
        ...WarehouseFields
      }
      currentTask {
        taskId
        name
        projectId
        originalTaskId
        project {
          projectId
          name
        }
      }
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
  ${MATERIAL_CATEGORY_FIELDS}
  ${WAREHOUSE_FIELDS}
`;

export const getEquipmentUsageHistory = gql`
  query getEquipmentUsageHistory($assetId: String!) {
    getEquipmentUsageHistory(assetId: $assetId) {
      ...EquipmentUsageFields
    }
  }
  ${EQUIPMENT_USAGE_FIELDS}
`;

export const getAllEquipmentUsageHistory = gql`
  query getAllEquipmentUsageHistory($filter: EquipmentUsageFilterInput) {
    getAllEquipmentUsageHistory(filter: $filter) {
      usageId
      assetId
      asset {
        assetId
        name
        assetCode
        categoryId
        category {
          categoryId
          name
          icon
          color
        }
        warehouseId
        warehouse {
          warehouseId
          name
        }
      }
      taskId
      task {
        taskId
        name
        projectId
        project {
          projectId
          name
        }
      }
      checkoutDate
      returnDate
      expectedReturnDate
      hoursUsed
      chargeToClient
      costPerHour
      pricePerHour
      condition
      notes
      checkoutNotes
      checkoutUserId
      checkoutUser {
        userId
        firstName
        lastName
      }
      returnUserId
      returnUser {
        userId
        firstName
        lastName
      }
      createdAt
    }
  }
`;

export const createEquipmentAsset = gql`
  mutation createEquipmentAsset($input: EquipmentAssetCreateInput!) {
    createEquipmentAsset(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const updateEquipmentAsset = gql`
  mutation updateEquipmentAsset($input: EquipmentAssetUpdateInput!) {
    updateEquipmentAsset(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const checkoutEquipment = gql`
  mutation checkoutEquipment($input: EquipmentCheckoutInput!) {
    checkoutEquipment(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const returnEquipment = gql`
  mutation returnEquipment($input: EquipmentReturnInput!) {
    returnEquipment(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const reserveEquipment = gql`
  mutation reserveEquipment($input: EquipmentReserveInput!) {
    reserveEquipment(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const setEquipmentMaintenance = gql`
  mutation setEquipmentMaintenance($input: EquipmentMaintenanceInput!) {
    setEquipmentMaintenance(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const completeEquipmentMaintenance = gql`
  mutation completeEquipmentMaintenance($assetId: String!) {
    completeEquipmentMaintenance(assetId: $assetId) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const cancelEquipmentReservation = gql`
  mutation cancelEquipmentReservation($reservationId: String!) {
    cancelEquipmentReservation(reservationId: $reservationId) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const retireEquipment = gql`
  mutation retireEquipment($assetId: String!) {
    retireEquipment(assetId: $assetId) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

export const deleteEquipmentAsset = gql`
  mutation deleteEquipmentAsset($assetId: String!) {
    deleteEquipmentAsset(assetId: $assetId) {
      affected
    }
  }
`;

// ==================== EQUIPMENT RESERVATION QUERIES ====================

export const getEquipmentReservations = gql`
  query getEquipmentReservations($assetId: String!) {
    getEquipmentReservations(assetId: $assetId) {
      ...EquipmentReservationFields
    }
  }
  ${EQUIPMENT_RESERVATION_FIELDS}
`;

export const getEquipmentReservationsByTask = gql`
  query getEquipmentReservationsByTask($taskId: String!) {
    getEquipmentReservationsByTask(taskId: $taskId) {
      ...EquipmentReservationFields
    }
  }
  ${EQUIPMENT_RESERVATION_FIELDS}
`;

export const getEquipmentReservationsForRange = gql`
  query getEquipmentReservationsForRange($assetId: String!, $rangeStart: DateTime!, $rangeEnd: DateTime!) {
    getEquipmentReservationsForRange(assetId: $assetId, rangeStart: $rangeStart, rangeEnd: $rangeEnd) {
      ...EquipmentReservationFields
    }
  }
  ${EQUIPMENT_RESERVATION_FIELDS}
`;

export const getReservationsByTaskForRange = gql`
  query getReservationsByTaskForRange($taskId: String!, $rangeStart: DateTime!, $rangeEnd: DateTime!) {
    getReservationsByTaskForRange(taskId: $taskId, rangeStart: $rangeStart, rangeEnd: $rangeEnd) {
      ...EquipmentReservationFields
    }
  }
  ${EQUIPMENT_RESERVATION_FIELDS}
`;

export const updateEquipmentReservation = gql`
  mutation updateEquipmentReservation($input: EquipmentUpdateReservationInput!) {
    updateEquipmentReservation(input: $input) {
      ...EquipmentAssetFields
    }
  }
  ${EQUIPMENT_ASSET_FIELDS}
`;

// ==================== LEAD TIME ALERTS ====================

export const getLeadTimeAlerts = gql`
  query getLeadTimeAlerts {
    getLeadTimeAlerts {
      taskMaterialId
      materialName
      taskName
      taskId
      projectName
      projectId
      leadTimeDays
      taskStartDate
      orderByDate
      daysUntilDeadline
      alertLevel
      quantity
      unit
      supplier
      catalogId
      warehouseId
    }
  }
`;

export const getLeadTimeAlertCounts = gql`
  query getLeadTimeAlertCounts {
    getLeadTimeAlertCounts {
      overdue
      urgent
      upcoming
      total
    }
  }
`;

// ==================== EXPORT DEFAULT ====================

const WarehouseGraphQL = {
  queries: {
    // Warehouse
    getWarehouses,
    getWarehouse,
    // Categories
    getMaterialCategories,
    getMaterialCategoriesByType,
    getGlobalMaterialCategories,
    // Catalog
    getMaterialCatalog,
    getMaterialCatalogItem,
    searchMaterialCatalog,
    getMaterialCatalogByCalculatorType,
    // Stock Levels
    getStockLevels,
    getStockLevelsByWarehouse,
    getStockLevel,
    getStockSummary,
    getLowStockItems,
    // Transactions
    getStockTransactions,
    // Task Materials
    getTaskMaterials,
    getTaskMaterialSummary,
    getLeadTimeAlerts,
    getLeadTimeAlertCounts,
    // Task Equipment
    getTaskEquipments,
    getTaskEquipment,
    getTaskEquipmentSummary,
    // Equipment
    getEquipmentAssets,
    getEquipmentAssetsByStatus,
    getAvailableEquipment,
    getEquipmentByTask,
    getEquipmentAsset,
    getEquipmentUsageHistory,
    getAllEquipmentUsageHistory,
    // Equipment Reservations
    getEquipmentReservations,
    getEquipmentReservationsByTask,
    getEquipmentReservationsForRange,
    getReservationsByTaskForRange,
  },
  mutations: {
    // Warehouse
    createWarehouse,
    updateWarehouse,
    setDefaultWarehouse,
    deleteWarehouse,
    // Categories
    createMaterialCategory,
    updateMaterialCategory,
    deleteMaterialCategory,
    createGlobalMaterialCategory,
    updateGlobalMaterialCategory,
    deleteGlobalMaterialCategory,
    // Catalog
    createMaterialCatalogItem,
    updateMaterialCatalogItem,
    deleteMaterialCatalogItem,
    // Stock Operations
    receiveStock,
    consumeStock,
    adjustStock,
    reserveStock,
    releaseReservation,
    returnStock,
    transferStock,
    // Task Materials
    createTaskMaterialManual,
    createTaskMaterialFromWarehouse,
    createTaskMaterialPlanned,
    updateTaskMaterial,
    returnTaskMaterial,
    deleteTaskMaterial,
    reserveTaskMaterial,
    consumeTaskMaterial,
    // Task Equipment
    createTaskEquipmentManual,
    createTaskEquipmentFromWarehouse,
    createTaskEquipmentPlanned,
    updateTaskEquipment,
    assignTaskEquipmentAsset,
    checkoutTaskEquipment,
    returnTaskEquipment,
    deleteTaskEquipment,
    // Equipment
    createEquipmentAsset,
    updateEquipmentAsset,
    checkoutEquipment,
    returnEquipment,
    reserveEquipment,
    setEquipmentMaintenance,
    completeEquipmentMaintenance,
    updateEquipmentReservation,
    cancelEquipmentReservation,
    retireEquipment,
    deleteEquipmentAsset,
  },
};

export default WarehouseGraphQL;
